import { createUnsavedForms } from './unsavedForms'

const guards = new WeakMap()

export function installFormLeaveGuard(router, confirm, hasForm, environment) {
  guards.get(router)?.dispose()
  const env = environment || (typeof window === 'undefined' ? null : { document, window })
  const tracker = !hasForm && env ? createUnsavedForms(env.document, env.window) : null
  const hasChanges = hasForm || (() => tracker?.hasChanges() || false)
  let question = null
  let permit = null
  const clearPermit = value => { if (permit === value) permit = null }
  const confirmLeave = async (from = router.currentRoute.value) => {
    if (from.name === 'login' || !hasChanges()) return true
    if (!question) question = Promise.resolve().then(confirm).then(Boolean).finally(() => { question = null })
    return question
  }
  const removeBefore = router.beforeEach((to, from) => {
    if (to.fullPath === from.fullPath) return true
    if (permit?.from === from.fullPath) { permit = null; return true }
    return confirmLeave(from)
  })
  const beforeUnload = event => {
    if (router.currentRoute.value.name === 'login' || !hasChanges()) return
    event.preventDefault()
    event.returnValue = ''
  }
  env?.window.addEventListener('beforeunload', beforeUnload)
  const removeReset = router.afterEach((to, from, failure) => {
    if (!failure && to.fullPath !== from.fullPath) tracker?.reset()
  })
  const controller = {
    confirmLeave,
    hasChanges,
    saved(form) { return tracker?.saved(form?.$el || form) },
    navigateSaved(method, ...args) {
      const value = { from: router.currentRoute.value.fullPath }
      permit = value
      const removeAfter = router.afterEach(() => cleanup())
      const cleanup = () => { clearPermit(value); removeAfter(); clearTimeout(timer) }
      // back()/go() return void; avoid leaving a permit if there is no history entry.
      const timer = setTimeout(cleanup, 1000)
      try {
        const result = router[method](...args)
        if (result?.then) result.then(cleanup, cleanup)
        return result
      } catch (error) { cleanup(); throw error }
    },
    dispose() {
      removeBefore(); removeReset(); tracker?.dispose()
      env?.window.removeEventListener('beforeunload', beforeUnload)
      permit = null; guards.delete(router)
    },
  }
  guards.set(router, controller)
  return controller.dispose
}

export const confirmFormExit = router => guards.get(router)?.confirmLeave() ?? Promise.resolve(true)
export const markFormSaved = (router, form) => guards.get(router)?.saved(form)
export const hasUnsavedChanges = router => guards.get(router)?.hasChanges() || false

// Call only from the successful save branch, at the actual navigation point.
export const navigateAfterSave = (router, method, ...args) => {
  const guard = guards.get(router)
  return guard ? guard.navigateSaved(method, ...args) : router[method](...args)
}
